import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { MoreVertical, Pencil, Plus, Radio, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import type { Theme } from "@/data/config";
import { usePortal } from "@/features/portal/portal-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteThemeDialog } from "@/features/theming/delete-theme-dialog";

/** Small "Live" pill shown on the brand's active theme. */
function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#28d07a]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[.04em] text-[#28d07a]">
      <span className="size-[5px] rounded-full bg-[#28d07a] shadow-[0_0_6px_#28d07a]" />
      Live
    </span>
  );
}

/** Mini palette dots so a theme is identifiable at a glance. */
function PaletteDots({ theme }: { theme: Theme }) {
  const keys = ["background", "surface", "primary", "accent"] as const;
  return (
    <span className="flex gap-[3px]">
      {keys.map((k) => (
        <span
          key={k}
          className="size-2.5 rounded-full border border-[rgba(255,255,255,.16)]"
          style={{ background: theme[k] }}
        />
      ))}
    </span>
  );
}

/** Horizontal selector of a brand's themes: pick one to edit, see which is live,
 *  and add / rename / set-live / delete. Lives at the top of the Theming page. */
export function ThemeStrip() {
  const {
    activeBrand,
    selectedThemeId,
    addTheme,
    renameTheme,
    deleteTheme,
    setLiveTheme,
    dirty,
  } = usePortal();
  const [, setSearchParams] = useSearchParams();

  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [deleting, setDeleting] = React.useState<Theme | null>(null);

  // Theme selection is URL-driven: setting `?theme` lets BrandRoute load that
  // theme's draft (and makes the selection shareable / refresh-safe).
  function selectTheme(themeId: string) {
    // Push (not replace) so the browser back button steps through theme switches.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("theme", themeId);
      return next;
    });
  }

  if (!activeBrand) return null;
  const { themes, activeThemeId } = activeBrand;
  const single = themes.length <= 1;

  function startRename(theme: Theme) {
    setRenamingId(theme.id);
    setRenameValue(theme.name);
  }

  function commitRename() {
    if (renamingId) renameTheme(renamingId, renameValue);
    setRenamingId(null);
  }

  return (
    <div className="mb-6">
      <div className="mb-2.5 text-[11px] font-bold uppercase tracking-[.08em] text-text-3">
        Themes
      </div>
      <div className="flex flex-wrap items-stretch gap-2.5">
        {themes.map((theme) => {
          const selected = theme.id === selectedThemeId;
          const live = theme.id === activeThemeId;
          const renaming = renamingId === theme.id;

          return (
            <div
              key={theme.id}
              className={cn(
                "group flex items-center gap-2.5 rounded-[12px] border px-3 py-2 transition-all",
                selected
                  ? "border-brand bg-panel-2 ring-[3px] ring-ring"
                  : "border-border bg-field hover:border-border-strong",
              )}
            >
              {renaming ? (
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename();
                    if (e.key === "Escape") setRenamingId(null);
                  }}
                  maxLength={40}
                  className="w-[140px] rounded-[7px] border border-brand bg-field px-2 py-1 text-[13px] font-semibold text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => selectTheme(theme.id)}
                  onDoubleClick={() => startRename(theme)}
                  className="flex cursor-pointer items-center gap-2.5 text-left outline-none"
                >
                  <PaletteDots theme={theme} />
                  <span
                    className={cn(
                      "text-[13px] font-semibold",
                      selected ? "text-foreground" : "text-muted-foreground group-hover:text-foreground",
                    )}
                  >
                    {theme.name}
                  </span>
                  {live && <LiveBadge />}
                  {selected && dirty && (
                    <span title="Unsaved changes" className="size-[7px] shrink-0 rounded-full bg-[#f5b945]" />
                  )}
                </button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger
                  className="grid size-6 shrink-0 cursor-pointer place-items-center rounded-md text-text-3 outline-none transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring"
                  aria-label={`${theme.name} options`}
                >
                  <MoreVertical className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setLiveTheme(theme.id)} disabled={live}>
                    <Radio className="text-brand" /> {live ? "Currently live" : "Set as live"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => startRename(theme)}>
                    <Pencil /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setDeleting(theme)}
                    disabled={live || single}
                    className="text-[#ff6b6b] focus:bg-[#ff6b6b]/10"
                  >
                    <Trash2 /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}

        <button
          type="button"
          // Create the theme, then select it via `?theme` so the editor opens it.
          onClick={() => void addTheme().then((id) => id && selectTheme(id))}
          className="flex items-center gap-2 rounded-[12px] border-[1.5px] border-dashed border-border-strong px-3.5 py-2 text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          <Plus className="size-4" /> New theme
        </button>
      </div>

      <DeleteThemeDialog
        theme={deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={(theme) => {
          // If the deleted theme was selected, follow the fallback into `?theme`.
          void deleteTheme(theme.id).then((nextId) => nextId && selectTheme(nextId));
          setDeleting(null);
        }}
      />
    </div>
  );
}
